import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { DepositEntry } from '@/types';
import { useState } from 'react';
import { Copy } from 'lucide-react';
import {
  useGetTransactionHistoryQuery,
  useUpdateTransactionStatusMutation,
} from '@/store/api/walletTransactionApi';
import { mapBackendStatusToAdmin } from '@/utils/transactionStatusMapper';

export default function DepositList() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = useGetTransactionHistoryQuery({
    page: pageIndex + 1, // ✅ 0-based → 1-based
    limit: pageSize,
    type: 'Credit',
    status: 'Pending',
  });

  const total = data?.pagination?.totalItems ?? 0;

  const [updateStatus, { isLoading: isUpdating }] = useUpdateTransactionStatusMutation();

  const handleApprove = async (id: number) => {
    await updateStatus({ id, action: 'Approved' }).unwrap();
  };

  const handleReject = async (id: number) => {
    await updateStatus({ id, action: 'Rejected' }).unwrap();
  };

  const depositData: DepositEntry[] =
    data?.data.map((tx) => ({
      id: tx.id.toString(),
      userId: tx.bank?.userId?.toString() ?? '',
      username: `${tx.wallet.user.firstname} ${tx.wallet.user.lastname}`,
      utrNumber: tx.transactionCode,
      amount: tx.amount,
      paymentMode: tx.bank ? 'Bank' : 'UPI',
      screenshotUrl: tx.imageProof ?? '',
      status: mapBackendStatusToAdmin(tx.status),
      requestedAt: tx.createdAt,
    })) ?? [];

  const depositColumns: ColumnDef<DepositEntry>[] = [
    {
      header: 'Sr.',
      cell: ({ row }) => pageIndex * pageSize + row.index + 1, // ✅ global index
    },
    {
      accessorKey: 'username',
      header: 'User Name',
      cell: ({ row }) => (
        <span className="text-blue-500 cursor-pointer hover:underline">
          {row.original.username}
        </span>
      ),
    },
    {
      accessorKey: 'utrNumber',
      header: 'UTR No.',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{row.original.utrNumber}</span>
          <button
            onClick={() => navigator.clipboard.writeText(row.original.utrNumber)}
            className="text-xs text-muted-foreground"
          >
            <Copy size={16} />
          </button>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => `₹${row.original.amount.toLocaleString()}`,
    },
    {
      accessorKey: 'paymentMode',
      header: 'Payment Mode',
    },
    {
      header: 'Screenshot',
      cell: () => <button className="text-blue-500">View</button>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = row.original.status;
        const color =
          s === 'Approved'
            ? 'text-green-600'
            : s === 'Pending'
              ? 'text-yellow-600'
              : 'text-red-600';

        return <span className={color}>{s}</span>;
      },
    },
    {
      accessorKey: 'requestedAt',
      header: 'Requested At',
      cell: ({ row }) => new Date(row.original.requestedAt).toLocaleString('en-IN'),
    },
    {
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={row.original.status !== 'Pending' || isUpdating}
            className="bg-green-600 hover:bg-green-700"
            onClick={() => handleApprove(Number(row.original.id))}
          >
            Approve
          </Button>

          <Button
            size="sm"
            variant="destructive"
            disabled={row.original.status !== 'Pending' || isUpdating}
            onClick={() => handleReject(Number(row.original.id))}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  const handleExport = (rows: DepositEntry[]) => {
    if (!rows.length) return;

    const header = [
      'Sr no',
      'User Name',
      'UTR No.',
      'Amount',
      'Payment Mode',
      'Screenshot',
      'Status',
      'Requested At',
    ];

    const csvRows = rows.map((d) =>
      [
        d.id,
        d.username,
        d.utrNumber,
        d.amount,
        d.paymentMode,
        d.screenshotUrl,
        d.status,
        d.requestedAt,
      ].join(',')
    );

    const csvString = [header.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'deposit_requests.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h5 className="text-xl font-semibold tracking-tight">Deposit Requests</h5>
          <p className="text-muted-foreground">
            Track all player deposit attempts including successful, pending, and failed
            transactions.
          </p>
        </div>
      </div>

      <DataTable
        columns={depositColumns}
        data={depositData}
        enableSearch
        enableExport
        enableFilters
        onExport={handleExport}
        pageIndex={pageIndex}
        pageSize={pageSize}
        total={total}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    </div>
  );
}
